import { useEffect, useRef, useState } from 'react';
import { datasetsApi } from '../lib/api';
import { extractLonLatZ } from '../lib/dataset-points3d';
import { useOptionalToast } from '../components/Toast';

const POINT_LIMIT = 200000; // the server's default page; one request per dataset
const EMPTY = { clouds: {}, errors: {} };

function detailOf(err) {
  const d = err?.response?.data?.detail;
  return (typeof d === 'string' && d) || err?.message || 'request failed';
}

/**
 * Fetches the points of each dataset once (kept as compact arrays) and keeps a failure per dataset:
 * { clouds: id -> extract, errors: id -> message }. A rejected request, or a dataset without usable
 * x/y/z values, sets an error and raises one toast.
 */
export default function useDatasetPoints(datasets) {
  const [state, setState] = useState(EMPTY);
  const requested = useRef(new Set());
  const toast = useOptionalToast();
  const showToast = toast?.showToast;

  useEffect(() => {
    datasets.forEach((d) => {
      if (requested.current.has(d.id)) return;
      requested.current.add(d.id);
      const fail = (detail) => {
        setState(prev => ({ ...prev, errors: { ...prev.errors, [d.id]: detail } }));
        if (showToast) showToast(`Could not load points for ${d.name || d.filename || d.id}: ${detail}`, 'error', 5000);
      };
      datasetsApi.points(d.id, { limit: POINT_LIMIT })
        .then((res) => {
          const ext = extractLonLatZ(res.data);
          if (!ext.count) { fail('no usable x/y/z values'); return; }
          setState(prev => ({ ...prev, clouds: { ...prev.clouds, [d.id]: ext } }));
        })
        .catch((err) => fail(detailOf(err)));
    });
  }, [datasets, showToast]);

  return state;
}
